import { useMemo } from 'react';
import {
  UserSpace,
  Meter,
  Consumption,
  SortParams,
  SortParamsArg,
} from 'models';
import { createTabsConfig } from 'utils';
import { useTranslation } from 'react-i18next';
import { consumptionTableCells } from 'configs';

import { PageTitle } from 'containers';
import {
  PageLoader,
  Tabs,
  Select,
  FiltersBar,
  DatePicker,
  NoData,
  Loader,
  Centered,
  Table,
  ControlWrapper,
} from 'components';
import { Box } from '@material-ui/core';

import { useStyles } from './style';

interface Props {
  spaces: UserSpace[];
  spacesLoading: boolean;
  meters: Meter[];
  metersLoading: boolean;
  consumptions: Consumption[];
  consumptionsLoading: boolean;
  startDate: Date | null;
  endDate: Date | null;
  selectedSpaceId: string | null;
  selectedMeter: Meter | null;
  sortParams: SortParams;
  handleChangeCurrentMeter: (meter: Meter) => void;
  handleChangeSortParams: (params: SortParamsArg) => void;
  handleChangeStartDate: (date: Date | null) => void;
  handleChangeEndDate: (date: Date | null) => void;
  handleChangeSpace: (id: string) => void;
}

const ConsumptionDetails: React.FC<Props> = ({
  spaces,
  spacesLoading,
  meters,
  metersLoading,
  consumptions,
  consumptionsLoading,
  startDate,
  endDate,
  selectedSpaceId,
  selectedMeter,
  handleChangeCurrentMeter,
  handleChangeStartDate,
  handleChangeEndDate,
  handleChangeSpace,
  handleChangeSortParams,
  sortParams,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tabCls = useMemo(
    () => ({
      ul: classes.tabs,

      /* eslint-disable-next-line */
    }),
    []
  );
  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },

      /* eslint-disable-next-line */
    }),
    []
  );

  const tabsConfig = useMemo(() => {
    return createTabsConfig(spaces);
  }, [spaces]);

  if (spacesLoading) {
    return <PageLoader />;
  }

  if (!spaces.length || !selectedSpaceId) {
    return <NoData />;
  }

  const renderTable = () => {
    return (
      <Table
        list={consumptions}
        loading={consumptionsLoading}
        tableCells={consumptionTableCells}
        order={sortParams.order}
        orderBy={sortParams.orderBy}
        handleChangeSortParams={handleChangeSortParams}
      />
    );
  };

  const renderFilterBar = () => {
    return (
      <FiltersBar className={classes.filtersBar}>
        <Box display="flex" flexWrap="wrap">
          <ControlWrapper>
            <span className={classes.label}>
              {`${t('#portal.consumption.bar.label.meterid')}:`}
            </span>
            <Select
              value={selectedMeter}
              options={meters}
              labelField={'MeterId'}
              valueField={'Id'}
              onSelect={handleChangeCurrentMeter}
              cls={selectCls}
            />
          </ControlWrapper>
          <ControlWrapper>
            <span className={classes.label}>
              {t('#portal.consumption.bar.label.startdate')}:
            </span>
            <DatePicker
              date={startDate}
              handleChange={handleChangeStartDate}
              maxDate={endDate}
            />
          </ControlWrapper>
          <ControlWrapper>
            <span className={classes.label}>
              {t('#portal.consumption.bar.label.enddate')}:
            </span>
            <DatePicker
              date={endDate}
              handleChange={handleChangeEndDate}
              minDate={startDate}
            />
          </ControlWrapper>
        </Box>
        <Box display="flex" flexWrap="wrap">
          <ControlWrapper>
            <span className={classes.label}>
              {t('#portal.consumption.bar.label.type')}
              {':'}
            </span>
            <span className={classes.value}>{selectedMeter?.Type?.Label}</span>
          </ControlWrapper>
          <ControlWrapper>
            <span className={classes.label}>
              {t('#portal.consumption.bar.label.multiplier')}
              {':'}
            </span>
            <span className={classes.value}>{selectedMeter?.Multiplier}</span>
          </ControlWrapper>
        </Box>
      </FiltersBar>
    );
  };

  const renderContent = () => {
    return meters.length ? (
      <>
        {renderFilterBar()}
        {renderTable()}
      </>
    ) : (
      <NoData />
    );
  };

  return (
    <>
      <PageTitle />
      <Tabs
        activeTab={selectedSpaceId}
        onSelect={handleChangeSpace}
        tabs={tabsConfig}
        cls={tabCls}
      />
      {metersLoading ? (
        <Centered>
          <Loader themeColor="darkBlue" size={30} />
        </Centered>
      ) : (
        renderContent()
      )}
    </>
  );
};

export default ConsumptionDetails;
